import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import {
  ControlPogResetIdentification,
  ControlPogResetTagOccupancy,
  ControlPogResetTaskDetailResponse,
  Store,
} from '../../types';
import { detectModInconsistency, isEndCapTag, ModInconsistencyType } from '../../utils/modTag';
import { PogResetFlagBadge, PogResetFlagBadges } from './components/PogResetFlagBadge';
import { ModDecision, PogResetModCard } from './components/PogResetModCard';

const DEFAULT_MATCH_THRESHOLD = 0.7;
const DEFAULT_SIMILAR_DELTA = 0.05;
const AT_MAIN_URL = process.env.REACT_APP_AT_MAIN_URL!;

// Asda doesn't use mirrored planograms, so the mirror toggle is hidden for its stores.
const ASDA_CHAIN_ID = 33;

/**
 * Resolve the ?from= marker a linking page sets to the view to return to.
 *
 * An allowlist of known origins rather than a raw path, so a hand-crafted URL cannot point
 * the back button somewhere unexpected.
 */
const backToFromParam = (from: string | null, storeName?: string): string => {
  const base = `/stores/${storeName}/pog-reset`;
  if (from === 'install-map' || from === 'mods') {
    return `${base}/${from}`;
  }

  return base;
};

// Human labels for detectModInconsistency results in the confirm dialog.
const MOD_INCONSISTENCY_LABELS: Record<ModInconsistencyType, string> = {
  location: 'different zone/aisle/side than the mods before it',
  gap: 'sequence gap vs the mods before it',
  direction: 'sequence direction flips vs the mods before it',
};

// PATCH .../resolve reasons for closing a task WITHOUT a mapping.
const REJECT_REASONS: { action: string; label: string }[] = [
  { action: 'UNABLE_TO_CHANGE', label: 'Not available in the store' },
  { action: 'INACCURATE_DETECTION', label: 'Number of mods don’t match' },
  { action: 'NOT_APPLICABLE', label: 'Not applicable' },
];

export const PogResetTaskDetailPage: React.FC = () => {
  const { storeName, taskId } = useParams<{ storeName: string; taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Task order carried over from the list page (its current filter/sort view).
  const taskIds: number[] = location.state?.taskIds ?? [];
  // Where this task was opened from — the mods table and install map link here too, so back
  // returns to the originating view. Router state is lost when a link opens in a new tab, so
  // ?from= stands in there; state wins for ordinary in-app navigation.
  const backTo: string =
    location.state?.backTo ??
    backToFromParam(new URLSearchParams(location.search).get('from'), storeName);
  const taskIndex = taskIds.indexOf(Number(taskId));
  const goToTask = (id: number) =>
    navigate(`/stores/${storeName}/pog-reset/${id}`, { state: { taskIds, backTo } });
  const { token, legacyToken } = useAuthContext();
  const { toggleLoading, showErrorToast } = useNotificationContext();

  const { showSuccessToast } = useNotificationContext();
  const [detail, setDetail] = useState<ControlPogResetTaskDetailResponse | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [matchThreshold, setMatchThreshold] = useState(DEFAULT_MATCH_THRESHOLD);
  const [similarDelta, setSimilarDelta] = useState(DEFAULT_SIMILAR_DELTA);
  const [countActiveEmbeddings, setCountActiveEmbeddings] = useState(true);
  // Per-mod mapping decisions (keyed by mod number), mirror toggle, submit state.
  const [decisions, setDecisions] = useState<Record<number, ModDecision | null>>({});
  const [isMirrored, setIsMirrored] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  // Assignment state re-fetched when the dialog opens: the detail payload's snapshot can
  // be minutes old, and another reviewer may have claimed a tag in the meantime.
  const [liveOccupancy, setLiveOccupancy] = useState<Record<string, ControlPogResetTagOccupancy>>(
    {}
  );
  const [isCheckingOccupancy, setIsCheckingOccupancy] = useState(false);
  // Store-wide install-v2 mod-center clicks, fetched once and shared by every mod card
  // (they all need the same data — fetching per card issued one request per mod).
  const [identifications, setIdentifications] = useState<ControlPogResetIdentification[]>([]);
  const [reviewed, setReviewed] = useState(false);
  const [store, setStore] = useState<Store | undefined>(undefined);

  useEffect(() => {
    if (!storeName) return undefined;
    let active = true;
    // Cached; used only to resolve the store's timezone and chain.
    ApiService.fetchStores(token)
      .then((stores) => {
        if (active) setStore(stores.find((s) => s.storeName === storeName));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [token, storeName]);

  const mirrorSupported = store?.chainId !== ASDA_CHAIN_ID;

  useEffect(() => {
    if (!storeName || !taskId) return undefined;
    let active = true;
    // Clear the previous task's state immediately so nothing stale renders while
    // the new request is in flight.
    setDetail(null);
    setDecisions({});
    const load = async () => {
      toggleLoading(true);
      setLoaded(false);
      try {
        // Defaults match the backend's — only send params the user actually changed.
        const response = await ApiService.fetchPogResetTaskDetail(
          token,
          storeName,
          Number(taskId),
          {
            matchThreshold: matchThreshold !== DEFAULT_MATCH_THRESHOLD ? matchThreshold : undefined,
            similarDelta: similarDelta !== DEFAULT_SIMILAR_DELTA ? similarDelta : undefined,
            // Always fetch embedding counts; the checkbox only toggles their visibility.
            countActiveEmbeddings: true,
          }
        );
        // Ignore a superseded request so an older response can't clobber newer state.
        if (!active) return;
        setDetail(response);
        setLoaded(true);
      } catch (err) {
        if (!active) return;
        showErrorToast(
          err instanceof Error ? err.message : 'Error loading planogram reset mappings'
        );
      } finally {
        if (active) toggleLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeName, taskId, token, matchThreshold, similarDelta]);

  // Seed each mod's decision from its proposal whenever a task loads. Only confident
  // proposals are auto-accepted; a proposal flagged `no_match` (best score below the
  // match threshold) is left unseeded, so the reviewer must consciously accept it (or
  // pick another candidate) rather than it silently counting as mapped.
  useEffect(() => {
    if (!detail) return;
    const seeded: Record<number, ModDecision | null> = {};
    // A closed task shows the mapping that was actually applied, not fresh proposals.
    if (detail.confirmedMapping) {
      for (const mod of detail.confirmedMapping.mods) {
        seeded[mod.modNumber] =
          mod.tagName == null
            ? { source: 'missing', tagName: null, modCoverageId: null }
            : {
                source: (mod.source as ModDecision['source']) ?? 'manual',
                tagName: mod.tagName,
                modCoverageId: null,
              };
      }
      setDecisions(seeded);
      setIsMirrored(detail.confirmedMapping.isMirrored);
      setRejectReason('');
      setReviewed(detail.reviewed);
      return;
    }

    for (const mod of detail.mods) {
      if (mod.modNumber == null) continue;
      // Only seed a mapped decision when the proposal has a real shelf-tag name —
      // pog-reset/start resolves mods by tag name, so a coverage id must never be
      // sent in its place. A tag-less proposal is left unmapped for manual review.
      // Ambiguous scans (several tags confirmed at install) are never auto-seeded,
      // UNLESS the backend settled the tie with evidence (scanResolved: sequence fit,
      // rivals assigned elsewhere, ...) — then the winner seeds like a clean scan.
      seeded[mod.modNumber] =
        mod.proposed?.tagName &&
        !mod.flags.includes('no_match') &&
        (!mod.proposed.scanAmbiguous || mod.proposed.scanResolved)
          ? {
              source: mod.proposed.method === 'scan' ? 'scan' : 'proposal',
              tagName: mod.proposed.tagName,
              modCoverageId: mod.proposed.modCoverageId,
            }
          : null;
    }
    setDecisions(seeded);
    setIsMirrored(false);
    setRejectReason('');
    setReviewed(detail.reviewed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, loaded]);

  const modsWithImages = detail?.mods.filter((mod) => mod.planogramImageUri) ?? [];

  // Ordered slot list (by mod number) for the pog-reset/start call; every mod needs a
  // decision (a tag or explicitly missing), and at least one mod must have a real tag.
  useEffect(() => {
    if (!storeName) return undefined;
    let active = true;
    ApiService.fetchPogResetIdentifications(token, storeName)
      .then((data) => {
        if (active) setIdentifications(data.identifications);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [token, storeName]);

  const orderedMods = detail
    ? [...detail.mods].sort((a, b) => (a.modNumber ?? 0) - (b.modNumber ?? 0))
    : [];

  const displayNumbers = new Map<number, number>();
  orderedMods.forEach((mod, index) => {
    if (mod.modNumber != null) {
      displayNumbers.set(mod.modNumber, index + 1);
    }
  });
  const displayNumberFor = (modNumber: number | null | undefined) =>
    modNumber != null ? (displayNumbers.get(modNumber) ?? modNumber) : '?';
  const allDecided =
    orderedMods.length > 0 &&
    orderedMods.every((mod) => mod.modNumber != null && decisions[mod.modNumber] != null);
  const hasRealTag = orderedMods.some(
    (mod) => mod.modNumber != null && decisions[mod.modNumber]?.tagName
  );
  const allMapped = allDecided && hasRealTag;

  // Tags assigned to more than one mod. Confirming would map several mods to the same
  // tag, so this blocks the confirm entirely (unlike the advisory warnings below).
  const assignedTagCounts = new Map<string, number>();
  for (const mod of orderedMods) {
    const tag = mod.modNumber != null ? decisions[mod.modNumber]?.tagName : null;
    if (tag) {
      const key = tag.toUpperCase();
      assignedTagCounts.set(key, (assignedTagCounts.get(key) ?? 0) + 1);
    }
  }

  const duplicateTagNames = new Set(
    [...assignedTagCounts].filter(([, count]) => count > 1).map(([tag]) => tag)
  );

  // Live sequence check against the tags chosen for the preceding mods — recomputed on
  // every decision change, unlike the server's `wrong_sequence` flag which is fixed to
  // the stored proposals. Same rule the confirm dialog applies, kept in one place.
  // Indexed by position in mod order, not render order — `detail.mods` is unsorted.
  const inconsistencyForModIndex = (index: number): ModInconsistencyType | null => {
    const mod = orderedMods[index];
    const tagName = (mod?.modNumber != null && decisions[mod.modNumber]?.tagName) || null;
    if (!tagName) return null;

    // An end cap doesn't follow the aisle's location/sequence run, so it's exempt when
    // it sits where end caps belong — as the first or last mod.
    if (isEndCapTag(tagName) && (index === 0 || index === orderedMods.length - 1)) {
      return null;
    }

    const preceding = orderedMods.slice(0, index);
    const precedingTags = preceding
      .map((m) => (m.modNumber != null && decisions[m.modNumber]?.tagName) || null)
      // A leading end cap is expected to differ from the aisle run — it must not become
      // the baseline that flags the second mod as being in the wrong location.
      .filter((tag, i): tag is string => Boolean(tag) && !(i === 0 && isEndCapTag(tag!)));
    if (precedingTags.length === 0) return null;
    const raw = detectModInconsistency(tagName, precedingTags);
    // Mods with no tag are skipped when building `precedingTags`, which makes the run look
    // shorter than it is — so a counter that legitimately continues past them reads as a
    // gap. Any untagged predecessor (marked missing in store, or simply not decided yet)
    // therefore suppresses the gap check; location/direction stay meaningful.
    const hasUntaggedPredecessor = preceding.some(
      (m) => m.modNumber == null || !decisions[m.modNumber]?.tagName
    );
    if (hasUntaggedPredecessor && raw?.type === 'gap') return null;
    return raw?.type ?? null;
  };

  // Every warning the mod cards show for the tag a reviewer actually chose, so the
  // confirm step repeats them instead of relying on the reviewer having scrolled past.
  const warningsForMod = (
    mod: ControlPogResetTaskDetailResponse['mods'][number]
  ): { text: string; title?: string }[] => {
    const decision = mod.modNumber != null ? decisions[mod.modNumber] : null;
    const tagName = decision?.tagName;
    if (!tagName) return [];
    const warnings: { text: string; title?: string }[] = [];

    // The chosen tag's row: a PR candidate, a scan option, or neither (manual entry).
    const candidate = mod.candidates.find((c) => c.tagName === tagName);
    const scan = mod.scanCandidates.find((s) => s.tagName === tagName);
    const proposed = mod.proposed?.tagName === tagName ? mod.proposed : null;

    // Live check wins over the payload snapshot; `exists === false` means the tag isn't
    // a camera-covered mod in this store at all.
    const live = liveOccupancy[tagName.toUpperCase()];
    const assignedName =
      live?.assignedPogName ??
      candidate?.assignedPogName ??
      scan?.assignedPogName ??
      proposed?.assignedPogName ??
      null;
    const assignedNumber =
      live?.assignedPogNumber ??
      candidate?.assignedPogNumber ??
      scan?.assignedPogNumber ??
      proposed?.assignedPogNumber ??
      null;
    if (assignedNumber) {
      warnings.push({
        text: 'already assigned to an active planogram',
        title: `${assignedName ?? ''} (${assignedNumber})`,
      });
    }
    if (live && !live.exists) {
      warnings.push({ text: 'no camera-covered mod with this tag in the store' });
    }

    const claim = candidate?.claimedBy ?? scan?.claimedBy ?? proposed?.claimedBy ?? null;
    if (claim) {
      warnings.push({
        text: `also proposed to task ${claim.taskId}`,
        title: `${claim.pogName ?? ''} (${claim.pogNumber ?? ''}) mod ${claim.modNumber ?? '?'}`,
      });
    }
    if (candidate?.isTrackingExcluded || proposed?.isTrackingExcluded) {
      warnings.push({ text: 'tracking off — camera evidence may be stale' });
    }
    // Only meaningful when the tag resolved to a known row; a manual tag has neither.
    const signOffRow = candidate ?? scan ?? proposed;
    if (signOffRow && !signOffRow.isSignedOff) {
      warnings.push({ text: 'aisle not signed off by a technician' });
    }
    if (proposed?.method === 'scan' && proposed.scanAmbiguous && !proposed.scanResolved) {
      warnings.push({ text: 'ambiguous field scan — more than one tag was scanned' });
    }
    if (
      proposed != null &&
      proposed.method !== 'scan' &&
      proposed.score != null &&
      proposed.score < matchThreshold
    ) {
      warnings.push({ text: 'proposal scored below the match threshold' });
    }
    if (decision?.source === 'manual') {
      warnings.push({ text: 'entered manually — not backed by a candidate or scan' });
    }
    return warnings;
  };

  // Fresh assignment check before showing the dialog; failure is non-blocking (the
  // payload snapshot still renders) so a flaky lookup can't stop a valid confirm.
  const openConfirmDialog = async () => {
    if (!storeName) return;
    const tagNames = Array.from(
      new Set(
        orderedMods
          .map((mod) => (mod.modNumber != null ? decisions[mod.modNumber]?.tagName : null))
          .filter((tag): tag is string => Boolean(tag))
      )
    );
    setShowConfirmDialog(true);
    if (tagNames.length === 0) return;
    setIsCheckingOccupancy(true);
    try {
      const response = await ApiService.fetchPogResetTagOccupancy(
        token,
        storeName,
        tagNames,
        Number(taskId)
      );
      setLiveOccupancy(
        Object.fromEntries(response.tags.map((tag) => [tag.tagName.toUpperCase(), tag]))
      );
    } catch {
      setLiveOccupancy({});
    } finally {
      setIsCheckingOccupancy(false);
    }
  };

  const handleConfirm = async () => {
    if (!storeName || !taskId || !allMapped || duplicateTagNames.size > 0 || isSubmitting) {
      return;
    }

    setShowConfirmDialog(false);
    setIsSubmitting(true);
    try {
      const tagNames = orderedMods.map((mod) => decisions[mod.modNumber as number]!.tagName);
      // POST start first; a 404 means a mod tag was not found — stop and surface it.
      await ApiService.startPogReset(token, storeName, {
        taskId: Number(taskId),
        tagNames,
        isMirrored,
      });
      await ApiService.resolvePogResetTask(legacyToken, storeName, Number(taskId), {
        action: 'APPROVED',
        pogResetModUpdates: {},
      });
      try {
        await ApiService.savePogResetConfirmedMapping(token, storeName, Number(taskId), {
          isMirrored,
          mods: orderedMods.map((mod) => {
            const decision = decisions[mod.modNumber as number]!;
            return {
              modNumber: mod.modNumber as number,
              tagName: decision.tagName,
              source: decision.source,
            };
          }),
        });
      } catch {
        // The mapping was applied; only the audit record failed to save.
      }
      showSuccessToast('Planogram reset mappings confirmed');
      navigate(backTo);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error confirming mappings');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!storeName || !taskId || !rejectReason || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await ApiService.resolvePogResetTask(legacyToken, storeName, Number(taskId), {
        action: rejectReason,
      });
      showSuccessToast('Task closed');
      navigate(backTo);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error closing task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleReviewed = async () => {
    if (!storeName || !taskId || isSubmitting) return;
    const next = !reviewed;
    setReviewed(next); // optimistic
    try {
      await ApiService.setPogResetTaskReviewed(token, storeName, Number(taskId), next);
      showSuccessToast(next ? 'Marked as reviewed' : 'Review mark removed');
    } catch (err) {
      setReviewed(!next); // revert
      showErrorToast(err instanceof Error ? err.message : 'Error updating reviewed status');
    }
  };

  // Keyboard review: j/k scroll between mod cards, n/p jump between tasks.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'n' && taskIndex !== -1 && taskIndex < taskIds.length - 1) {
        goToTask(taskIds[taskIndex + 1]);
      } else if (e.key === 'p' && taskIndex > 0) {
        goToTask(taskIds[taskIndex - 1]);
      } else if (e.key === 'j' || e.key === 'k') {
        const elements = (detail?.mods ?? [])
          .map((mod) => document.getElementById(`pog-reset-mod-${displayNumberFor(mod.modNumber)}`))
          .filter(Boolean) as HTMLElement[];
        if (elements.length === 0) return;
        const target =
          e.key === 'j'
            ? elements.find((el) => el.getBoundingClientRect().top > 80)
            : [...elements].reverse().find((el) => el.getBoundingClientRect().top < -80);
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, taskIndex, taskIds]);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-[96rem] mx-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <Link to={backTo} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md">
              <ArrowBackIcon />
            </Link>
            {taskIndex !== -1 && taskIds.length > 1 && (
              <div className="flex items-center gap-1 text-sm text-gray-600">
                <button
                  onClick={() => goToTask(taskIds[taskIndex - 1])}
                  disabled={taskIndex === 0}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded-md disabled:opacity-30 disabled:cursor-default"
                  title="Previous task"
                >
                  <ChevronLeftIcon />
                </button>
                <span
                  className="whitespace-nowrap"
                  title="Keyboard: n/p = next/prev task, j/k = next/prev mod"
                >
                  {taskIndex + 1} of {taskIds.length}
                </span>
                <button
                  onClick={() => goToTask(taskIds[taskIndex + 1])}
                  disabled={taskIndex === taskIds.length - 1}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded-md disabled:opacity-30 disabled:cursor-default"
                  title="Next task"
                >
                  <ChevronRightIcon />
                </button>
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                {detail?.importedPog?.pogName
                  ? `Planogram Reset — ${detail.importedPog.pogName}`
                  : `Planogram Reset — Task ${taskId}`}
              </h1>
              {!detail?.computedAt && loaded && detail && (
                <p className="text-sm text-gray-600">
                  Mappings have not been generated for this task yet — showing the planogram only.
                </p>
              )}
            </div>
          </div>
          {/* Review controls — the interactive knobs, grouped and kept apart from
              the read-only compute config (which lives in the info panel below). */}
          <div className="flex items-center gap-3 text-sm bg-white rounded-lg shadow-sm border px-3 py-2">
            <label
              className="flex items-center gap-2 text-gray-600"
              title="Minimum score for a mod's best candidate to count as a match. Below it the mod is flagged “No Match”; a mod at or above it counts toward the task's match status."
            >
              Match threshold
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={matchThreshold}
                onChange={(e) => setMatchThreshold(Number(e.target.value))}
                className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <span className="h-5 w-px bg-gray-200" />
            <label
              className="flex items-center gap-2 text-gray-600"
              title="Only affects the “Multiple Similar Candidates” warning: raised when the top two candidates' scores differ by less than this (e.g. delta 0.05 → 95% and 91% warn, 95% and 89% don't)."
            >
              Similar delta
              <input
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={similarDelta}
                onChange={(e) => setSimilarDelta(Number(e.target.value))}
                className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <span className="h-5 w-px bg-gray-200" />
            <label
              className="flex items-center gap-2 text-gray-600"
              title="Show active embedding crops per product on the planogram (orange < 5, green ≥ 5)"
            >
              <input
                type="checkbox"
                checked={countActiveEmbeddings}
                onChange={(e) => setCountActiveEmbeddings(e.target.checked)}
              />
              Active embeddings
            </label>
          </div>
        </div>

        {detail && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-3">
              {[
                {
                  label: 'Store',
                  value: (() => {
                    const name = detail.storeLegibleName ?? detail.storeName;
                    if (!name) return null;
                    // storeLegibleName often already embeds the code (e.g. "ASDA Grantham (4165)").
                    const alreadyHasCode = detail.storeCode && name.includes(detail.storeCode);
                    return detail.storeCode && !alreadyHasCode
                      ? `${name} (${detail.storeCode})`
                      : name;
                  })(),
                },
                { label: 'Planogram Name', value: detail.importedPog?.pogName },
                { label: 'Planogram Number', value: detail.importedPog?.pogNumber },
                { label: 'Category', value: detail.importedPog?.assortmentGroup },
                { label: 'Department', value: detail.importedPog?.departmentName },
                { label: 'Start Date', value: detail.importedPog?.startDate },
                {
                  label: 'Calculated',
                  value: detail.computedAt ? new Date(detail.computedAt).toLocaleString() : null,
                },
                // Once a task is closed the Action Task link is no longer actionable.
                ...(detail.confirmedMapping
                  ? []
                  : [
                      {
                        label: 'Action Task',
                        value: (
                          <a
                            href={`${AT_MAIN_URL}/pog-reset/${detail.taskId}?storeName=${storeName}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            {detail.taskDate ?? detail.taskId} <OpenInNewIcon fontSize="inherit" />
                          </a>
                        ),
                      },
                    ]),
              ].map(({ label, value }) => (
                <div key={label}>
                  <div className="text-xs text-gray-500">{label}</div>
                  <div className="text-sm font-medium">{value ?? '—'}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-3 pt-3 border-t flex-wrap">
              <span className="text-sm font-medium text-gray-600">Status:</span>
              <PogResetFlagBadge flag={detail.status} />
              {/* Mirrors the Automated tab's gate, so the reviewer can see at a glance
                  whether this task would be applied without review. */}
              {detail.automationReady && (
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 cursor-help"
                  title="Every mod proposed at sufficient confidence with no issues, every aisle signed off, not assigned elsewhere — this task can be applied without review"
                >
                  Automated
                </span>
              )}
              {detail.issues.length > 0 && (
                <>
                  <span className="text-sm font-medium text-gray-600 ml-2">Issues:</span>
                  <PogResetFlagBadges flags={detail.issues} />
                </>
              )}
              {detail.config && (
                <div className="flex items-center gap-2 text-xs text-gray-500 ml-auto">
                  <span
                    className="text-gray-400 cursor-help"
                    title="Read-only settings the cached scores were produced with — changing any of them requires a recalculation."
                  >
                    Computed with:
                  </span>
                  <span
                    className="px-2 py-0.5 bg-gray-100 rounded-md cursor-help"
                    title="A detected product only counts toward matching if the model's confidence in its identity is at least this high."
                  >
                    Similarity ≥ {Math.round(detail.config.minCandidateSimilarity * 100)}%
                  </span>
                  <span
                    className="px-2 py-0.5 bg-gray-100 rounded-md cursor-help"
                    title="A mod needs at least this match score before the algorithm will suggest it as the proposed mapping."
                  >
                    Min proposal {Math.round(detail.config.minPairScore * 100)}%
                  </span>
                  {detail.config.includeCartonPolygons != null && (
                    <span
                      className="px-2 py-0.5 bg-gray-100 rounded-md cursor-help"
                      title="Whether carton (case-pack) detections were counted alongside individual products when matching."
                    >
                      Cartons {detail.config.includeCartonPolygons ? 'on' : 'off'}
                    </span>
                  )}
                  {detail.config.tagPrefix && (
                    <span
                      className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md cursor-help"
                      title="The last recalculation restricted candidates to mods with this tag prefix; out-of-prefix results were merged from the previous run."
                    >
                      Prefix {detail.config.tagPrefix}*
                    </span>
                  )}
                  {detail.config.skipPr && (
                    <span
                      className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md cursor-help"
                      title="The last recalculation skipped product recognition — proposals come from field scans only; PR candidates were carried over from the previous full run."
                    >
                      PR skipped
                    </span>
                  )}
                  {detail.algorithmVersion && (
                    <span
                      className="px-2 py-0.5 bg-gray-100 rounded-md font-mono cursor-help"
                      title="Version tag of the matching algorithm + settings; a change invalidates the cache and triggers recompute."
                    >
                      {detail.algorithmVersion}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {detail?.importedPog?.pogName &&
          /(?<!\d)\d\.[1-9](?![\dmM])/.test(detail.importedPog.pogName) && (
            <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-lg shadow-md p-4 mb-4 text-sm font-medium">
              ⚠ This modular includes a half-bay (it does not cover an entire mod).
            </div>
          )}

        {detail?.importedPog?.pogName && /hot\s?spot/i.test(detail.importedPog.pogName) && (
          <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-lg shadow-md p-4 mb-4 text-sm font-medium">
            ⚠ This is a hotspot planogram (only covers the top shelf of a mod).
          </div>
        )}

        {(detail?.reviewedBy || detail?.reviewedAt) && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg shadow-md p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-purple-800">Reviewed</span>
              <span className="text-xs text-gray-500">
                {detail.reviewedBy ? `by ${detail.reviewedBy}` : ''}
                {detail.reviewedAt ? ` · ${new Date(detail.reviewedAt).toLocaleString()}` : ''}
              </span>
            </div>
          </div>
        )}

        {detail?.confirmedMapping && (
          <div className="bg-green-50 border border-green-200 rounded-lg shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-green-800">Applied mapping</span>
              <span className="text-xs text-gray-500">
                {mirrorSupported &&
                  (detail.confirmedMapping.isMirrored ? 'Mirrored' : 'Not mirrored')}
                {detail.confirmedMapping.confirmedBy
                  ? `${mirrorSupported ? ' · ' : ''}by ${detail.confirmedMapping.confirmedBy}`
                  : ''}
                {detail.confirmedMapping.confirmedAt
                  ? ` · ${new Date(detail.confirmedMapping.confirmedAt).toLocaleString()}`
                  : ''}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {detail.confirmedMapping.mods.map((mod) => (
                <span
                  key={mod.modNumber}
                  className={`px-2 py-1 text-xs bg-white border rounded-md ${
                    mod.source === 'missing' ? 'border-amber-300' : 'border-green-300'
                  }`}
                  title={
                    mod.source === 'missing'
                      ? 'Marked missing in store — no POG applied'
                      : mod.source === 'manual' || mod.source === 'candidate'
                        ? 'Overridden by reviewer'
                        : 'Accepted suggestion'
                  }
                >
                  Mod {displayNumberFor(mod.modNumber)}:{' '}
                  {mod.tagName ? (
                    <span className="font-mono font-medium">{mod.tagName}</span>
                  ) : (
                    <span className="font-medium text-amber-700">missing</span>
                  )}
                  {mod.source !== 'proposal' && mod.source !== 'missing' && (
                    <span className="ml-1 text-amber-600">({mod.source})</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {modsWithImages.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="text-sm font-medium text-gray-600 mb-2">
              Planogram overview — click a bay to jump to it
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {modsWithImages.map((mod, index) => (
                <button
                  key={mod.modNumber ?? `overview-${index}`}
                  onClick={() =>
                    document
                      .getElementById(`pog-reset-mod-${displayNumberFor(mod.modNumber)}`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                  className="flex-shrink-0 text-center group relative"
                >
                  <img
                    src={mod.planogramImageUri!}
                    alt={`Mod ${displayNumberFor(mod.modNumber)}`}
                    className="h-32 border rounded-md group-hover:ring-2 group-hover:ring-blue-400"
                  />
                  {mod.candidates.length > 0 && (
                    <span
                      className={`absolute top-1 right-1 px-1.5 py-0.5 rounded text-xs font-semibold text-white ${
                        mod.flags.length === 0
                          ? 'bg-green-600/90'
                          : mod.flags.includes('missing_candidate')
                            ? 'bg-red-600/90'
                            : 'bg-yellow-500/90'
                      }`}
                      title={mod.flags.length > 0 ? mod.flags.join(', ') : 'No issues'}
                    >
                      {Math.round((mod.candidates[0]?.score ?? 0) * 100)}%
                    </span>
                  )}
                  {mod.candidates.length === 0 && detail?.computedAt && (
                    <span
                      className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-xs font-semibold text-white bg-red-600/90"
                      title="No candidates"
                    >
                      —
                    </span>
                  )}
                  <div className="text-xs text-gray-600 mt-1">
                    {mod.modNumber != null && decisions[mod.modNumber] != null ? (
                      decisions[mod.modNumber]?.tagName ? (
                        <span className="text-gray-900">
                          {`${displayNumberFor(mod.modNumber)}: ${decisions[mod.modNumber]?.tagName}`}
                        </span>
                      ) : (
                        <span className="text-amber-700">
                          {`${displayNumberFor(mod.modNumber)}: Missing`}
                        </span>
                      )
                    ) : (
                      <span className="text-blue-700">
                        {`Mod ${displayNumberFor(mod.modNumber)}`}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {loaded && !detail && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            No data for this task. Generate mappings from the task list first.
          </div>
        )}

        <div className={reviewed ? 'opacity-50' : ''}>
          {detail?.mods.map((mod, index) => (
            <PogResetModCard
              key={`${taskId}-${mod.modNumber ?? `mod-${index}`}`}
              mod={mod}
              displayNumber={displayNumberFor(mod.modNumber)}
              readOnly={!!detail.confirmedMapping}
              matchThreshold={matchThreshold}
              minCandidateSimilarity={detail.config?.minCandidateSimilarity ?? 0.8}
              showEmbeddingsCount={countActiveEmbeddings}
              storeName={storeName ?? ''}
              storeTimeZone={store?.timeZone}
              decision={mod.modNumber != null ? (decisions[mod.modNumber] ?? null) : null}
              sequenceInconsistency={inconsistencyForModIndex(
                orderedMods.findIndex((m) => m.modNumber === mod.modNumber)
              )}
              identifications={identifications}
              onDecisionChange={(decision) =>
                mod.modNumber != null &&
                setDecisions((prev) => ({ ...prev, [mod.modNumber as number]: decision }))
              }
            />
          ))}
        </div>

        {detail && detail.mods.length > 0 && !detail.confirmedMapping && (
          <div className="sticky bottom-0 bg-white border-t shadow-lg rounded-t-lg p-4 mt-4 flex flex-wrap items-center gap-4">
            {mirrorSupported && (
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={isMirrored}
                  onChange={(e) => setIsMirrored(e.target.checked)}
                />
                Mirror planogram
              </label>
            )}
            <button
              onClick={openConfirmDialog}
              disabled={!allMapped || isSubmitting}
              title={
                allMapped
                  ? 'Save mappings and close the task'
                  : 'Assign a mod to every planogram mod first'
              }
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm mappings (
              {
                orderedMods.filter((m) => m.modNumber != null && decisions[m.modNumber] != null)
                  .length
              }
              /{orderedMods.length})
            </button>
            <button
              onClick={handleToggleReviewed}
              disabled={isSubmitting}
              title="Mark this task as reviewed to filter it out of the list — does not change the mapping"
              className={`px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                reviewed
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'border border-purple-600 text-purple-600 hover:bg-purple-50'
              }`}
            >
              {reviewed ? 'Reviewed ✓' : 'Mark as reviewed'}
            </button>
            <div className="flex-1" />
            <span className="text-sm text-gray-500">Can’t map?</span>
            <select
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a reason…</option>
              {REJECT_REASONS.map((reason) => (
                <option key={reason.action} value={reason.action}>
                  {reason.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleReject}
              disabled={!rejectReason || isSubmitting}
              className="px-4 py-2 border border-red-600 text-red-600 rounded-md hover:bg-red-50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Close without mapping
            </button>
          </div>
        )}

        {showConfirmDialog && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => setShowConfirmDialog(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-3">Confirm mappings</h3>
              <p className="text-sm text-gray-700">
                Are you sure those are correct tags
                {mirrorSupported && (
                  <>
                    {' '}
                    and the POG{' '}
                    <span className="font-semibold">{isMirrored ? 'is' : 'is not'}</span> mirrored
                  </>
                )}
                ?
              </p>
              {isCheckingOccupancy && (
                <p className="mt-2 text-xs text-gray-500">Re-checking tag assignments…</p>
              )}
              <ul className="mt-4 max-h-64 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100 text-sm">
                {orderedMods.map((mod, index) => {
                  const tagName =
                    (mod.modNumber != null && decisions[mod.modNumber]?.tagName) || null;
                  const isDuplicate =
                    tagName != null && duplicateTagNames.has(tagName.toUpperCase());
                  // A duplicate is the highest-priority issue and the only blocking one,
                  // so it replaces the mod's other warnings instead of stacking on them.
                  // Each mod's tag is checked against every tag before it in mod order.
                  const inconsistency = isDuplicate ? null : inconsistencyForModIndex(index);
                  const modWarnings = isDuplicate ? [] : warningsForMod(mod);
                  return (
                    <li key={mod.modNumber} className="px-3 py-1.5">
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Mod {displayNumberFor(mod.modNumber)}</span>
                        {tagName ? (
                          <span className="font-medium text-gray-900">{tagName}</span>
                        ) : (
                          <span className="font-medium text-amber-700">Missing in store</span>
                        )}
                      </div>
                      {isDuplicate && (
                        <div className="text-xs text-red-600 font-medium text-right mt-0.5">
                          ⚠ duplicate — this tag is assigned to more than one mod
                        </div>
                      )}
                      {inconsistency && (
                        <div className="text-xs text-amber-600 text-right mt-0.5">
                          ⚠ {MOD_INCONSISTENCY_LABELS[inconsistency]}
                        </div>
                      )}
                      {modWarnings.map((warning) => (
                        <div
                          key={warning.text}
                          className="text-xs text-red-600 text-right mt-0.5"
                          title={warning.title}
                        >
                          ⚠ {warning.text}
                        </div>
                      ))}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isSubmitting || duplicateTagNames.size > 0}
                  title={
                    duplicateTagNames.size > 0
                      ? 'Each tag can be assigned to only one mod — resolve the duplicates first'
                      : undefined
                  }
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
